/**
 * Analytics Manager - Coordinates multiple analytics providers
 */
export default class AnalyticsManager {
  constructor() {
    this.providers = new Map();
    this.isInitialized = false;
  }

  /**
   * Add an analytics provider
   * @param {AnalyticsProvider} provider - The analytics provider to add
   * @returns {AnalyticsManager} - For method chaining
   */
  addProvider(provider) {
    if (!provider || typeof provider.getName !== 'function') {
      throw new Error(
        'Provider must implement the AnalyticsProvider interface',
      );
    }

    const name = provider.getName();
    this.providers.set(name, provider);

    console.debug(`Analytics provider '${name}' added`);
    return this;
  }

  /**
   * Remove an analytics provider
   * @param {string} name - Name of the provider to remove
   * @returns {AnalyticsManager} - For method chaining
   */
  removeProvider(name) {
    this.providers.delete(name);
    console.debug(`Analytics provider '${name}' removed`);
    return this;
  }

  /**
   * Get a specific provider
   * @param {string} name - Name of the provider
   * @returns {AnalyticsProvider|undefined}
   */
  getProvider(name) {
    return this.providers.get(name);
  }

  /**
   * Get all providers
   * @returns {AnalyticsProvider[]}
   */
  getAllProviders() {
    return Array.from(this.providers.values());
  }

  /**
   * Initialize all providers
   * @returns {Promise<{success: string[], failed: string[]}>}
   */
  async initialize() {
    const results = {
      success: [],
      failed: [],
    };

    const initPromises = Array.from(this.providers.entries()).map(
      async ([name, provider]) => {
        try {
          if (!provider.isAvailable()) {
            console.warn(`Analytics provider '${name}' is not available`);
            results.failed.push(name);
            return;
          }

          const initialized = await provider.initialize();
          if (initialized) {
            results.success.push(name);
            console.debug(
              `Analytics provider '${name}' initialized successfully`,
            );
          } else {
            results.failed.push(name);
            console.warn(`Analytics provider '${name}' failed to initialize`);
          }
        } catch (error) {
          results.failed.push(name);
          console.error(
            `Error initializing analytics provider '${name}':`,
            error,
          );
        }
      },
    );

    await Promise.all(initPromises);

    this.isInitialized = true;
    console.debug('Analytics Manager initialization complete:', results);

    return results;
  }

  /**
   * Track an event across all initialized providers
   * @param {string} eventName - Name of the event
   * @param {Object} eventData - Event data
   * @param {string[]} [excludeProviders] - Provider names to exclude
   */
  trackEvent(eventName, eventData, excludeProviders = []) {
    if (!this.isInitialized) {
      console.warn(
        'Analytics Manager not initialized. Call initialize() first.',
      );
      return;
    }

    const promises = [];

    this.providers.forEach((provider, name) => {
      if (excludeProviders.includes(name)) {
        return;
      }

      if (!provider.isInitialized) {
        console.warn(
          `Provider '${name}' not initialized, skipping event tracking`,
        );
        return;
      }

      try {
        // Track events asynchronously to avoid blocking
        promises.push(
          Promise.resolve(provider.trackEvent(eventName, eventData)).catch(
            (error) => {
              console.error(
                `Error tracking event in provider '${name}':`,
                error,
              );
            },
          ),
        );
      } catch (error) {
        console.error(`Error tracking event in provider '${name}':`, error);
      }
    });

    Promise.all(promises).catch(() => {
      // Errors are already logged above
    });
  }

  /**
   * Set user properties across all initialized providers
   * @param {Object} properties - User properties
   * @param {string[]} [excludeProviders] - Provider names to exclude
   */
  setUserProperties(properties, excludeProviders = []) {
    if (!this.isInitialized) {
      console.warn(
        'Analytics Manager not initialized. Call initialize() first.',
      );
      return;
    }

    this.providers.forEach((provider, name) => {
      if (excludeProviders.includes(name)) {
        return;
      }

      if (!provider.isInitialized) {
        console.warn(
          `Provider '${name}' not initialized, skipping user properties`,
        );
        return;
      }

      try {
        provider.setUserProperties(properties);
      } catch (error) {
        console.error(
          `Error setting user properties in provider '${name}':`,
          error,
        );
      }
    });
  }

  /**
   * Execute a method on a specific provider
   * @param {string} providerName - Name of the provider
   * @param {string} methodName - Name of the method to call
   * @param {...*} args - Arguments to pass to the method
   * @returns {*} - Return value from the provider method
   */
  executeProviderMethod(providerName, methodName, ...args) {
    const provider = this.getProvider(providerName);

    if (!provider) {
      throw new Error(`Provider '${providerName}' not found`);
    }

    if (typeof provider[methodName] !== 'function') {
      throw new Error(
        `Method '${methodName}' not found on provider '${providerName}'`,
      );
    }

    return provider[methodName](...args);
  }
}
